/**
 * Servicio para notificaciones locales (fake) - Solo Frontend
 * 
 * Este servicio permite mostrar notificaciones sin backend:
 * - Desde la app en primer plano (Notification API)
 * - Desde el service worker cuando la app está minimizada
 * 
 * ⚠️ LIMITACIÓN: NO funciona si la app está completamente cerrada
 * Para eso necesitarías Push API + backend
 */

class LocalNotificationService {
	constructor() {
		this.isSupported = 'Notification' in window;
		this.serviceWorkerRegistration = null;
	}

	/**
	 * Inicializar el servicio
	 * Debe llamarse después de que el service worker esté registrado
	 */
	async initialize() {
		if (!this.isSupported) {
			console.warn('⚠️ Notification API no soportada');
			return false;
		}

		// Obtener el registro del service worker si está disponible
		if ('serviceWorker' in navigator) {
			try {
				this.serviceWorkerRegistration = await navigator.serviceWorker.ready;
				console.log('✅ Service Worker listo para notificaciones locales');
			} catch (error) {
				console.warn('⚠️ Service Worker no disponible:', error);
			}
		}

		return true;
	}

	/**
	 * Verificar si las notificaciones están soportadas
	 */
	isNotificationSupported() {
		return this.isSupported;
	}

	/**
	 * Obtener el estado del permiso actual
	 * @returns {string} 'default' | 'granted' | 'denied'
	 */
	getPermissionStatus() {
		if (!this.isSupported) {
			return 'denied';
		}
		return Notification.permission;
	}

	/**
	 * Solicitar permiso para notificaciones
	 * @returns {Promise<string>} 'granted' | 'denied' | 'default'
	 */
	async requestPermission() {
		if (!this.isSupported) {
			return 'denied';
		}

		// Si ya está concedido o denegado, retornar el estado actual
		if (Notification.permission !== 'default') {
			return Notification.permission;
		}

		try {
			const permission = await Notification.requestPermission();
			return permission;
		} catch (error) {
			console.error('❌ Error al solicitar permiso:', error);
			return 'denied';
		}
	}

	/**
	 * Mostrar notificación desde la app (primer plano)
	 * @param {string} title - Título de la notificación
	 * @param {object} options - Opciones de la notificación
	 */
	showNotificationFromApp(title, options = {}) {
		if (!this.isSupported) {
			console.error('❌ Notification API no soportada');
			return null;
		}

		if (Notification.permission !== 'granted') {
			console.warn('⚠️ Permiso de notificaciones no concedido');
			return null;
		}

		const defaultOptions = {
			body: '',
			icon: '/static/img/icons/icon-192x192.png',
			badge: '/static/img/icons/icon-72x72.png',
			tag: 'local-notification',
			requireInteraction: false,
			vibrate: [200, 100, 200],
			data: {}
		};

		const notificationOptions = {
			...defaultOptions,
			...options
		};

		try {
			const notification = new Notification(title, notificationOptions);
			
			// Manejar clic en la notificación
			notification.onclick = function(event) {
				event.preventDefault();
				console.log('🔔 Notificación clickeada desde app');
				
				// Si hay una URL en los datos, abrirla
				if (notificationOptions.data && notificationOptions.data.url) {
					window.focus();
					window.location.href = notificationOptions.data.url;
				} else {
					window.focus();
				}
				
				notification.close();
			};

			return notification;
		} catch (error) {
			console.error('❌ Error al mostrar notificación:', error);
			return null;
		}
	}

	/**
	 * Mostrar notificación desde el Service Worker (segundo plano)
	 * Envía un mensaje al service worker para que muestre la notificación
	 * @param {string} title - Título de la notificación
	 * @param {object} options - Opciones de la notificación
	 */
	async showNotificationFromServiceWorker(title, options = {}) {
		if (!this.serviceWorkerRegistration) {
			console.warn('⚠️ Service Worker no disponible, usando Notification API');
			return this.showNotificationFromApp(title, options);
		}

		if (Notification.permission !== 'granted') {
			console.warn('⚠️ Permiso de notificaciones no concedido');
			return null;
		}

		const message = {
			type: 'SHOW_NOTIFICATION',
			title: title,
			options: {
				body: options.body || '',
				icon: options.icon || '/static/img/icons/icon-192x192.png',
				badge: options.badge || '/static/img/icons/icon-72x72.png',
				tag: options.tag || 'local-notification',
				requireInteraction: options.requireInteraction || false,
				vibrate: options.vibrate || [200, 100, 200],
				data: options.data || {}
			}
		};

		try {
			// Enviar mensaje al service worker
			await this.serviceWorkerRegistration.active.postMessage(message);
			console.log('✅ Mensaje enviado al Service Worker para mostrar notificación');
			return true;
		} catch (error) {
			console.error('❌ Error al enviar mensaje al Service Worker:', error);
			// Fallback a Notification API
			return this.showNotificationFromApp(title, options);
		}
	}

	/**
	 * Mostrar notificación (intenta usar Service Worker, fallback a Notification API)
	 * @param {string} title - Título de la notificación
	 * @param {object} options - Opciones de la notificación
	 * @param {boolean} preferServiceWorker - Preferir Service Worker si está disponible
	 */
	async showNotification(title, options = {}, preferServiceWorker = true) {
		if (Notification.permission !== 'granted') {
			const permission = await this.requestPermission();
			if (permission !== 'granted') {
				console.warn('⚠️ Permiso denegado por el usuario');
				return null;
			}
		}

		if (preferServiceWorker && this.serviceWorkerRegistration) {
			return await this.showNotificationFromServiceWorker(title, options);
		} else {
			return this.showNotificationFromApp(title, options);
		}
	}

	/**
	 * Cerrar todas las notificaciones con un tag específico
	 * @param {string} tag - Tag de las notificaciones a cerrar
	 */
	async closeNotificationsByTag(tag) {
		if (this.serviceWorkerRegistration) {
			const message = {
				type: 'CLOSE_NOTIFICATIONS',
				tag: tag
			};
			await this.serviceWorkerRegistration.active.postMessage(message);
		}
	}
}

// Exportar instancia singleton
export default new LocalNotificationService();




// WEBPACK FOOTER //
// ./src/services/local-notifications.js